import { z } from 'zod';
import { dataHoraSchema, paginacaoSchema } from './comum.js';
/**
 * ============================================================================
 * IA — a chave fica no SERVIDOR
 * ============================================================================
 *
 * Item B-07 do backlog. Hoje os tres `vite.config.ts` fazem:
 *
 *     define: { 'process.env.API_KEY': JSON.stringify(env.GEMINI_API_KEY) }
 *
 * `define` e substituicao TEXTUAL: a chave e escrita em texto puro dentro do
 * JavaScript entregue a qualquer visitante. Qualquer pessoa abre o DevTools,
 * procura por `AIza` e queima a cota — que e paga.
 *
 * Com a chamada aqui, alem de a chave sair do bundle, passam a existir duas
 * coisas que antes eram impossiveis: medir custo de IA por cliente e aplicar
 * cota por conta.
 *
 * ============================================================================
 * O QUE ESTA SECAO ACRESCENTOU
 * ============================================================================
 *
 * A primeira versao media custo escrevendo `caracteresEntrada`/`caracteresSaida`
 * num `EventoAuditoria`. Caractere nao e token, e a conta do Google e por token:
 * o numero existia, parecia medicao e nao servia para somar dinheiro nenhum.
 *
 * Agora o custo sai do `usageMetadata` que o proprio Gemini devolve — token de
 * entrada, de saida, de raciocinio e de cache, separados — multiplicado pelo
 * preco do modelo. E o preco e DADO, nao codigo: mora numa tabela editavel com a
 * data em que foi conferido, porque tabela de preco de provedor muda sozinha e
 * preco chumbado em codigo vira mentira silenciosa na tela de custos.
 *
 * Quando um modelo e usado sem preco registrado, o custo volta `null` — nunca um
 * numero inventado. `null` na tela vira "preco nao cadastrado", que e acionavel;
 * um zero, ou uma estimativa, viraria relatorio errado.
 */
export const parteIaSchema = z.discriminatedUnion('tipo', [
    z.object({ tipo: z.literal('texto'), texto: z.string() }),
    z.object({
        tipo: z.literal('imagem'),
        mime: z.string(),
        /** base64 SEM o prefixo data:. Nao e persistido: vai para o modelo e morre. */
        base64: z.string(),
    }),
]);
/**
 * As tarefas que a API sabe instruir.
 *
 * `diagnostico` nao vem do navegador por `ia.gerar` — ela e escrita pelo proprio
 * teste de chave, e existe aqui porque o registro de uso a atribui do mesmo jeito
 * que as outras. O teste tambem gasta token, e gasto que nao aparece no relatorio
 * e gasto que ninguem consegue explicar no fim do mes.
 */
export const tarefaIaSchema = z.enum([
    'gerar-site',
    'editar-site',
    'texto-livre',
    'sugestoes',
    'diagnostico',
]);
/** De onde partiu a chamada. Serve para a tela separar esteira de uso manual. */
export const origemIaSchema = z.enum(['fabrica', 'painel', 'esteira', 'diagnostico']);
/** O consumo de tokens de UMA chamada, como o provedor reportou. */
export const usoIaSchema = z.object({
    id: z.string(),
    clienteId: z.uuid().nullable(),
    /** Nome do cliente no momento da leitura. Evita um join na tela. */
    clienteNome: z.string().nullable(),
    siteId: z.uuid().nullable(),
    usuarioId: z.string().nullable(),
    jobId: z.string().nullable(),
    /**
     * O lote de criacao em massa que pagou esta chamada. `null` fora dela.
     *
     * Sem esta coluna a pergunta "quanto custou o lote que acabei de mandar" so
     * teria resposta somando cliente por cliente no navegador — e a tela de
     * criacao em massa nem sabe quais clientes ja foram gerados, porque quem gera
     * e o worker, depois, fora da aba.
     */
    lote: z.string().nullable(),
    tarefa: tarefaIaSchema,
    origem: origemIaSchema,
    modelo: z.string(),
    tokensEntrada: z.number().int(),
    tokensSaida: z.number().int(),
    /** Tokens de raciocinio. O Gemini cobra por eles e nao os devolve no texto. */
    tokensRaciocinio: z.number().int(),
    /** Tokens servidos do cache do provedor, cobrados mais barato. */
    tokensCache: z.number().int(),
    tokensTotal: z.number().int(),
    /** `null` = nao havia preco cadastrado para este modelo. Nunca zero por engano. */
    custoUsd: z.number().nullable(),
    custoBrl: z.number().nullable(),
    ms: z.number().int(),
    sucesso: z.boolean(),
    erro: z.string().nullable(),
    criadoEm: dataHoraSchema,
});
export const gerarComIaSchema = z.object({
    /**
     * `gerar-site` cria a pagina inteira do zero; `editar-site` mexe numa que ja
     * existe. Separadas porque a instrucao de sistema e outra — pedir "retorne o
     * HTML COMPLETO atualizado" para quem nao recebeu HTML nenhum e o tipo de
     * prompt que devolve desculpa em vez de pagina.
     */
    tarefa: z.enum(['gerar-site', 'editar-site', 'texto-livre', 'sugestoes']),
    partes: z.array(parteIaSchema).min(1).max(20),
    /** Para atribuir custo. Opcional porque nem toda chamada e de um cliente. */
    clienteId: z.uuid().optional(),
    /**
     * Modelo, quando quem chama quer forcar um.
     *
     * Ausente = o escolhido na plataforma (`ia.config`). O navegador PODE escolher
     * porque a escolha nao e um segredo — ela e visivel na tela de IA e o custo de
     * cada modelo esta ao lado. O que o navegador nunca decide e a chave.
     */
    modelo: z.string().min(1).max(120).optional(),
    origem: origemIaSchema.default('painel'),
    /**
     * URLs que o MODELO deve buscar e ler antes de responder.
     *
     * Liga a ferramenta `url_context` do Gemini. Sem ela, mandar a lista de
     * enderecos dentro do texto do prompt faz o modelo responder sobre os
     * enderecos, nao sobre o CONTEUDO deles — e a resposta parece boa, porque ele
     * inventa a partir do dominio.
     *
     * Existe porque a Central de Conhecimento do painel usava isto por um caminho
     * proprio: `nyroh-sites/server.ts` mantinha um segundo cliente do Gemini com
     * `process.env.GEMINI_API_KEY || ''` — fallback de credencial que o CLAUDE.md
     * proibe — chamando o provedor sem contrato, sem guard, sem cota e sem
     * nenhuma medicao de custo. Metade do gasto de IA da empresa saia por ali e
     * nao aparecia em relatorio nenhum.
     */
    urlsDeContexto: z.array(z.url()).max(20).optional(),
});
/** O que a resposta conta sobre o consumo, sem obrigar uma segunda consulta. */
export const consumoDaChamadaSchema = z.object({
    modelo: z.string(),
    tokensEntrada: z.number().int(),
    tokensSaida: z.number().int(),
    tokensRaciocinio: z.number().int(),
    tokensCache: z.number().int(),
    tokensTotal: z.number().int(),
    custoUsd: z.number().nullable(),
    custoBrl: z.number().nullable(),
    ms: z.number().int(),
    /** `false` quando o modelo usado nao tem preco cadastrado. A tela avisa. */
    precoConhecido: z.boolean(),
});
export const respostaIaSchema = z.object({
    texto: z.string(),
    modelo: z.string(),
    /** Bloco ```html extraido, quando houver. Evita o front reparsear a string. */
    html: z.string().nullable(),
    /**
     * O que o modelo conseguiu (ou nao) ler de `urlsDeContexto`.
     *
     * Vazio quando nao se pediu URL nenhuma. A tela mostra isto porque uma URL que
     * o provedor NAO conseguiu buscar produz uma resposta que parece completa e
     * foi escrita sem a fonte — o pior modo de falha possivel numa base de
     * conhecimento.
     */
    urlsLidas: z
        .array(z.object({ url: z.string(), status: z.string() }))
        .default([]),
    /** O gasto desta chamada, ja registrado no banco. */
    consumo: consumoDaChamadaSchema,
});
// ---------------------------------------------------------------- config --
/** Onde a chave em uso foi encontrada. A tela mostra isto em voz alta. */
export const fonteChaveSchema = z.enum(['banco', 'ambiente', 'nenhuma']);
/**
 * O modo da esteira.
 *
 * `ia` = o worker pede a pagina ao Gemini e so cai no template quando a IA nao
 * responde. `template` = nunca chama a IA. Existe porque o caminho inteiro
 * (spec -> render -> R2 -> borda) tem de continuar exercitavel sem gastar cota,
 * e porque um lote de 100 mil sites com IA e uma decisao de dinheiro que merece
 * um interruptor visivel em vez de uma variavel de ambiente.
 */
export const modoGeracaoSchema = z.enum(['ia', 'template']);
export const configIaSchema = z.object({
    /** NUNCA a chave. So de onde ela vem e como ela termina. */
    fonteChave: fonteChaveSchema,
    /**
     * Os quatro ultimos caracteres da chave gravada no banco.
     *
     * Quatro caracteres nao permitem reconstruir nada e resolvem a unica pergunta
     * que a tela precisa responder: "a chave que esta ai e a que eu colei?".
     */
    chaveSufixo: z.string().nullable(),
    chaveAtualizadaEm: dataHoraSchema.nullable(),
    /** Se existe `GEMINI_API_KEY` no ambiente da API, para servir de queda. */
    temChaveNoAmbiente: z.boolean(),
    /**
     * `false` quando `CHAVE_CIFRA_DADOS` nao esta definida na API.
     *
     * Sem ela nao ha como cifrar em repouso, e gravar a chave em texto puro seria
     * reintroduzir o PIN em claro que este repositorio ja tirou do banco. A rota de
     * gravacao recusa, e a tela precisa poder dizer por que o campo esta travado.
     */
    podeGuardarChave: z.boolean(),
    modelo: z.string(),
    modoGeracao: modoGeracaoSchema,
    /** Cambio usado para exibir custo em reais. O custo em dolar e o que o Google cobra. */
    cotacaoDolar: z.number(),
    /**
     * Cota diaria do recurso `gemini`, quando definida por
     * `pnpm --filter @nyroh/api definir-cota gemini <limite>`.
     *
     * `null` = nenhuma cota cadastrada, e a IA roda sem teto. Dito na tela, porque
     * "sem teto" numa esteira de 100 mil sites e uma fatura, nao uma configuracao.
     */
    cota: z
        .object({
        limiteDia: z.number().int(),
        usadoHoje: z.number().int(),
        pausado: z.boolean(),
    })
        .nullable(),
    atualizadoEm: dataHoraSchema,
});
export const salvarConfigIaSchema = z
    .object({
    /**
     * A chave em claro, so na ENTRADA e so uma vez.
     *
     * Ela e cifrada com AES-256-GCM antes de encostar no banco e nunca volta em
     * resposta nenhuma. String vazia APAGA a chave do banco — que e como se volta
     * a usar a do ambiente sem precisar de acesso ao servidor.
     */
    chave: z.string().max(400).optional(),
    modelo: z.string().min(1).max(120).optional(),
    modoGeracao: modoGeracaoSchema.optional(),
    cotacaoDolar: z.number().positive().max(1000).optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'informe pelo menos um campo para alterar',
});
// --------------------------------------------------------------- modelos --
/**
 * O preco de um modelo, em dolar por MILHAO de tokens.
 *
 * Milhao e a unidade em que o Google publica; converter na hora de cadastrar
 * seria mais uma chance de errar uma casa decimal.
 */
export const precoModeloSchema = z.object({
    usdEntradaMi: z.number().nonnegative(),
    usdSaidaMi: z.number().nonnegative(),
    /** Preco de raciocinio quando cobrado a parte. Ausente = cobrado como saida. */
    usdRaciocinioMi: z.number().nonnegative().nullable(),
    /** Data em que este preco foi conferido no provedor. A tela mostra. */
    conferidoEm: z.string(),
    fonte: z.string(),
});
export const salvarPrecoModeloSchema = z.object({
    usdEntradaMi: z.number().nonnegative().max(10_000),
    usdSaidaMi: z.number().nonnegative().max(10_000),
    usdRaciocinioMi: z.number().nonnegative().max(10_000).nullable().optional(),
    conferidoEm: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'use AAAA-MM-DD').optional(),
    fonte: z.string().max(300).optional(),
});
/**
 * ============================================================================
 * O PRECO VINDO DO GOOGLE — Cloud Billing Catalog API
 * ============================================================================
 *
 * A API do Gemini devolve TOKEN, nunca dinheiro: nao existe campo de custo na
 * resposta de `generateContent`, nem no `usageMetadata`. Todo codigo que "pega o
 * preco da API" na verdade multiplica os tokens por um numero escrito no proprio
 * arquivo — e esse numero envelhece em silencio.
 *
 * Quem publica preco de forma consultavel e a **Cloud Billing Catalog API**
 * (`cloudbilling.googleapis.com/v1/services/{id}/skus`), com o servico
 * `generativelanguage.googleapis.com`. E de la que estas rotas leem.
 *
 * POR QUE ISTO PROPOE EM VEZ DE GRAVAR DIRETO
 *
 * O catalogo nao fala em ID de modelo: ele fala em SKU, com descricao em texto
 * ("Gemini 2.5 Flash Input Tokens"), varias por modelo — uma por tier
 * (Standard, Batch, Flex), por regiao e por faixa de contexto. O casamento
 * SKU -> modelo e heuristico por construcao. Gravar direto trocaria "preco
 * digitado errado" por "preco importado do SKU errado", que e pior: parece
 * automatico, e ninguem confere numero que veio sozinho.
 *
 * Entao a rota de leitura MOSTRA o que casou, com a descricao do SKU e o preco
 * unitario cru ao lado do valor convertido, e a de escrita so grava o que o
 * operador marcou.
 */
export const skuDePrecoSchema = z.object({
    /** O que este SKU paga nesta proposta. */
    papel: z.enum(['entrada', 'saida', 'raciocinio']),
    id: z.string(),
    descricao: z.string(),
    /** Como o provedor vende: "tokens", "1M tokens", "characters"... */
    unidade: z.string(),
    /** O preco CRU, na unidade acima. E a prova de que a conversao esta certa. */
    precoUnitario: z.number(),
    /** Quantos SKUs disputavam este papel (regiao, tier). 1 = sem ambiguidade. */
    variantes: z.number().int(),
});
export const precoDoProvedorSchema = z.object({
    /** O id do modelo, como `GET /v1beta/models` o lista. */
    modelo: z.string(),
    usdEntradaMi: z.number().nullable(),
    usdSaidaMi: z.number().nullable(),
    /** Quando o provedor cobra raciocinio a parte. `null` = cobrado como saida. */
    usdRaciocinioMi: z.number().nullable(),
    skus: z.array(skuDePrecoSchema),
    /** O que esta cadastrado hoje, para a tela mostrar lado a lado. */
    atual: precoModeloSchema.nullable(),
    /** O preco do catalogo difere do cadastrado (ou nao ha cadastrado). */
    mudou: z.boolean(),
    /**
     * Da para aplicar: tem entrada E saida.
     *
     * Meio preco nao e preco: gravar so a entrada faria o custo sair menor que a
     * fatura com cara de medicao completa.
     */
    aplicavel: z.boolean(),
});
export const catalogoPrecosSchema = z.object({
    consultou: z.boolean(),
    /**
     * Qual chave foi usada.
     *
     * `gemini` significa que nao ha chave de faturamento propria e a chave do
     * Gemini foi tentada — ela funciona quando a Cloud Billing API esta habilitada
     * no mesmo projeto e a chave nao tem restricao de API. Quando nao funciona, o
     * `aviso` diz exatamente isso, em vez de um 403 cru.
     */
    fonteChave: z.enum(['faturamento', 'gemini', 'nenhuma']),
    aviso: z.string().nullable(),
    /** `services/XXXX-XXXX-XXXX` do catalogo, para quem quiser conferir na mao. */
    servico: z.string().nullable(),
    moeda: z.string(),
    propostas: z.array(precoDoProvedorSchema),
    /** SKU visto e descartado, com o motivo. E o que impede "sumiu e nao sei por que". */
    ignorados: z.array(z.object({ descricao: z.string(), motivo: z.string() })),
    consultadoEm: dataHoraSchema,
});
export const aplicarPrecosSchema = z.object({
    /** Os modelos marcados na tela. Nunca "aplique tudo" implicito. */
    modelos: z.array(z.string().min(1).max(120)).min(1).max(50),
});
export const resultadoAplicarPrecosSchema = z.object({
    aplicados: z.number().int(),
    /** Pedido para um modelo que o catalogo nao trouxe (ou trouxe pela metade). */
    naoAplicados: z.array(z.object({ modelo: z.string(), motivo: z.string() })),
});
/**
 * Um modelo como o PROVEDOR o lista, cruzado com o preco cadastrado aqui.
 *
 * A lista vem ao vivo de `GET /v1beta/models`, e nao de uma constante no codigo,
 * porque quais modelos existem depende da chave: modelo em previa, modelo ja
 * desligado e modelo liberado so para certas contas mudam de mes para mes. Uma
 * lista chumbada oferece na tela um modelo que a chave nao alcanca — e o erro so
 * aparece na primeira geracao do lote.
 */
export const modeloIaSchema = z.object({
    /** Sem o prefixo `models/`. E o que vai na URL de `generateContent`. */
    id: z.string(),
    nome: z.string(),
    descricao: z.string(),
    entradaMaxTokens: z.number().int().nullable(),
    saidaMaxTokens: z.number().int().nullable(),
    /** `null` = modelo sem preco cadastrado; o custo dele nao e calculavel. */
    preco: precoModeloSchema.nullable(),
    /** O que a plataforma esta usando agora. */
    emUso: z.boolean(),
});
export const listaModelosSchema = z.object({
    /**
     * `false` quando nao ha chave nenhuma: a lista volta vazia e a tela pede a
     * chave em vez de mostrar um catalogo vazio como se fosse resposta do provedor.
     */
    consultouProvedor: z.boolean(),
    fonteChave: fonteChaveSchema,
    /** Preenchido quando `consultouProvedor` e false, ou quando o provedor recusou. */
    aviso: z.string().nullable(),
    modelos: z.array(modeloIaSchema),
    /** Precos cadastrados que o provedor NAO listou (modelo desligado, por exemplo). */
    precosOrfaos: z.array(z.object({ modelo: z.string(), preco: precoModeloSchema })),
});
// ----------------------------------------------------------- diagnostico --
export const diagnosticarIaSchema = z.object({
    /** Ausente = testa o modelo escolhido na plataforma. */
    modelo: z.string().min(1).max(120).optional(),
    /**
     * Testa uma chave que ainda NAO foi salva.
     *
     * E a ordem certa: colar, testar, so entao gravar. Sem isto, descobrir que a
     * chave esta errada exige grava-la antes — e a chave anterior, que funcionava,
     * ja foi por cima.
     */
    chave: z.string().max(400).optional(),
});
export const diagnosticoIaSchema = z.object({
    ok: z.boolean(),
    fonteChave: fonteChaveSchema,
    chaveSufixo: z.string().nullable(),
    modelo: z.string(),
    /** O HTTP que o Google devolveu. `0` = nem chegou a sair (sem chave, rede). */
    statusHttp: z.number().int(),
    ms: z.number().int(),
    /** Sempre em portugues e sempre acionavel: diz o que fazer, nao so o que houve. */
    mensagem: z.string(),
    /** O que o modelo respondeu. Prova que a chave gera de verdade, nao so autentica. */
    resposta: z.string().nullable(),
    /** O gasto do proprio teste. Pequeno, e mesmo assim contabilizado. */
    consumo: consumoDaChamadaSchema.nullable(),
});
// ----------------------------------------------------------------- custo --
export const filtroUsoIaSchema = paginacaoSchema.extend({
    clienteId: z.uuid().optional(),
    modelo: z.string().max(120).optional(),
    origem: origemIaSchema.optional(),
    /** Rotulo do lote de criacao em massa. Filtra o gasto daquele envio. */
    lote: z.string().max(120).optional(),
    periodo: z.enum(['hoje', '7d', '30d', 'todos']).default('30d'),
});
/** Um site e o que ele custou de IA — a pergunta que a fabrica faz. */
export const custoPorSiteSchema = z.object({
    clienteId: z.uuid().nullable(),
    clienteNome: z.string(),
    clienteSlug: z.string().nullable(),
    siteId: z.uuid().nullable(),
    chamadas: z.number().int(),
    tokensEntrada: z.number().int(),
    tokensSaida: z.number().int(),
    tokensTotal: z.number().int(),
    custoUsd: z.number().nullable(),
    custoBrl: z.number().nullable(),
    /** Quantas chamadas deste site nao tinham preco cadastrado. */
    chamadasSemPreco: z.number().int(),
    falhas: z.number().int(),
    primeiraEm: dataHoraSchema,
    ultimaEm: dataHoraSchema,
});
export const filtroResumoCustoSchema = z.object({
    periodo: z.enum(['hoje', '7d', '30d', 'todos']).default('30d'),
    /**
     * Recorta o resumo em UM lote de criacao em massa.
     *
     * E o que a tela de criacao em massa mostra enquanto o worker trabalha: o
     * gasto real daquele envio, somado das linhas de `uso_ia` que o proprio
     * provedor reportou — nao uma estimativa por site.
     */
    lote: z.string().max(120).optional(),
});
export const resumoCustoIaSchema = z.object({
    periodo: z.enum(['hoje', '7d', '30d', 'todos']),
    /** O lote pedido, quando o resumo foi recortado por um. */
    lote: z.string().nullable(),
    chamadas: z.number().int(),
    falhas: z.number().int(),
    /** Chamadas cujo modelo nao tem preco cadastrado: o total abaixo NAO as inclui. */
    chamadasSemPreco: z.number().int(),
    tokensEntrada: z.number().int(),
    tokensSaida: z.number().int(),
    tokensRaciocinio: z.number().int(),
    tokensTotal: z.number().int(),
    custoUsd: z.number(),
    custoBrl: z.number(),
    /** Sites distintos que consumiram IA no periodo. Denominador do custo medio. */
    sitesAtendidos: z.number().int(),
    /** Custo medio por SITE, nao por chamada: um site pode custar varias chamadas. */
    custoMedioPorSiteBrl: z.number().nullable(),
    porModelo: z.array(z.object({
        modelo: z.string(),
        chamadas: z.number().int(),
        tokensTotal: z.number().int(),
        custoUsd: z.number().nullable(),
        custoBrl: z.number().nullable(),
    })),
    porDia: z.array(z.object({
        dia: z.string(),
        chamadas: z.number().int(),
        custoBrl: z.number(),
    })),
});
// ------------------------------------------------------------ gerar site --
/**
 * O pedido da ESTEIRA: gerar a pagina de um cliente que ja esta no banco.
 *
 * Diferente de `ia.gerar`, que recebe as partes prontas do navegador. Aqui quem
 * monta o prompt e o servidor, a partir do cadastro e do dossie — porque o worker
 * nao tem esses dados e nao deveria busca-los so para remonta-los.
 */
export const gerarSiteComIaSchema = z.object({
    clienteId: z.uuid(),
    modelo: z.string().min(1).max(120).optional(),
    origem: origemIaSchema.default('esteira'),
    /** Para o registro de uso apontar de volta para o job que gastou. */
    jobId: z.string().max(40).optional(),
    /**
     * O lote de criacao em massa, quando a geracao veio de um.
     *
     * Vai para `uso_ia.lote` na MESMA transacao que grava o consumo, e e o que
     * permite a tela do lote somar o gasto real dele sem varrer cliente a cliente.
     */
    lote: z.string().max(120).optional(),
});
export const respostaGerarSiteSchema = z.object({
    /**
     * `null` quando a IA nao produziu pagina.
     *
     * Nao e erro: e a resposta honesta de "nao deu, use o template". Devolver 502
     * aqui faria a esteira contar como falha um job que ainda vai terminar com um
     * site publicavel — e gastaria as cinco tentativas da fila para nada.
     */
    html: z.string().nullable(),
    /** Por que nao houve HTML. Vai para o log do worker e para a tela. */
    motivo: z
        .enum(['ok', 'sem_chave', 'modo_template', 'cota_esgotada', 'provedor_recusou', 'sem_html'])
        .default('ok'),
    modelo: z.string(),
    consumo: consumoDaChamadaSchema.nullable(),
    /**
     * Quantas fotos do cliente entraram no site.
     *
     * O prompt so cita `[REMOTE_IMG_n]` ate o numero que existe, e a substituicao
     * acontece aqui dentro. Devolver o numero e o que permite ao log do worker
     * dizer "publicado pela IA com 4 fotos" em vez de deixar "as fotos subiram mas
     * o site saiu com stock" como diferenca invisivel.
     */
    fotosUsadas: z.number().int().default(0),
});
//# sourceMappingURL=ia.js.map